import React, { useCallback, useEffect, useState } from "react";
import BankForm from "../Components/IfscForm/IfscForm";
import {
  deleteIfsc,
  getIfscList,
  viewIfscDetails,
} from "../../services/apiService";
import { Eye, Loader2, View } from "lucide-react";
import InfoViewerModal from "../Components/IfscForm/ViewModal";
import FormHeading from "../../components/formHeader";
import ConfirmModal from "../../components/Billing/ConfirmModal";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";

export default function BankIFSCMaster() {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalData, setTotaldata] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");
  const [loading, setLoading] = useState(false);

  const [tableData, setTableData] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerData, setViewerData] = useState({ label: "", value: "" });
  const [editClicked, setEditClicked] = useState(false);
  const [formType, setFormType] = useState("create");
  const [dataToUpdate, setDataToUpdate] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [idTodelete, setIdToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false)

  const openViewer = (label, value) => {
    setViewerData({ label, value });
    setViewerOpen(true);
  };

  const handleEdit = async (item) => {
    setEditClicked(true);
    setFormType("update");

    setLoading(true);

    try {
      const res = await viewIfscDetails({ id: item.id });

      if (res) {
        setDataToUpdate(res?.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);

    try {
      const res = await deleteIfsc({ id: idTodelete });

      if (res?.status?.code === 200) {
        showSuccessToast(
          res?.message || res?.data?.message || res?.data?.status?.message,
        );

        setIsDeleted(true)
      } else {
        showErrorToast(
          res?.message || res?.data?.message || res?.data?.status?.message,
        );
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
      setIsDeleting(false);

      setShowConfirmModal(false);
    }
  };

  const fetchAllIfsc = async () => {
    const payload = {
      page: currentPage,
      total_data: totalData,
      sort_order: sortOrder,
    };

    if (sortBy) payload.sort_by = sortBy;
    if (search) payload.search = search;

    setLoading(true);

    try {
      const res = await getIfscList(payload);

      if (res) {
        setTableData(res);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [search]);

  useEffect(() => {
    fetchAllIfsc();
  }, [currentPage, debouncedSearch, sortBy, sortOrder, totalData, isDeleted]);

  useEffect(() => {
    if (!editClicked) return;

    const el = document.getElementById("scrollIntoView");

    el.scrollIntoView({ behavior: "smooth" });

    const timeout = setTimeout(() => {
      setEditClicked(false);
    }, 200);

    return () => {
      clearTimeout(timeout);
    };
  }, [editClicked]);

  return (
    <div className="bg-gradient-to-br pt-8 from-blue-50 to-indigo-100 min-h-screen">
      <div id="scrollIntoView" />
      <div className=" mx-auto py-2 px-6">
        <FormHeading
          formType={formType}
          title={" Bank IFSC Master"}
          subtitle={"Create and manage bank IFSC records"}
        />

        <BankForm
          dataToUpdate={dataToUpdate}
          formType={formType}
          setFormType={setFormType}
        />

        {/* REPORT SECTION */}
        <div className=" bg-white shadow-xl rounded-2xl pt-2 pb-6 px-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
            {/* Title */}
            <h2 className="text-2xl font-bold text-indigo-700">
              Bank IFSC Report
            </h2>

            {/* Controls */}
            <div className="flex flex-wrap items-center gap-4">
              {/* Search */}
              <div className="flex flex-col text-sm">
                <label className="text-gray-600 mb-1">Search</label>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  type="search"
                  placeholder="Search IFSC..."
                  className="border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 w-48"
                />
              </div>

              {/* Total Records */}
              <div className="flex flex-col text-sm">
                <label className="text-gray-600 mb-1">Total Records</label>
                <select
                  value={totalData}
                  onChange={(e) => setTotaldata(e.target.value)}
                  className="border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                </select>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <div className="overflow-x-auto relative">
              <table className="min-w-full text-sm text-left border">
                <thead className="bg-indigo-600 text-white">
                  <tr>
                    <th className="px-4 py-3">SRN</th>
                    <th className="px-4 py-3">Edit</th>
                    <th className="px-4 py-3">Delete</th>
                    <th className="px-4 py-3">IFSC</th>
                    <th className="px-4 py-3">Bank Code</th>
                    <th className="px-4 py-3">Bank Name</th>
                    <th className="px-4 py-3">Branch</th>
                    <th className="px-4 py-3">Centre</th>
                    <th className="px-4 py-3">District</th>
                    <th className="px-4 py-3">State</th>
                    <th className="px-4 py-3">City</th>
                    <th className="px-4 py-3">MICR</th>
                    <th className="px-4 py-3">Swift</th>
                    <th className="px-4 py-3">ISO</th>
                    <th className="px-4 py-3">Contact</th>
                    <th className="px-4 py-3">Address</th>
                    <th className="px-4 py-3">IMPS</th>
                    <th className="px-4 py-3">NEFT</th>
                    <th className="px-4 py-3">RTGS</th>
                    <th className="px-4 py-3">UPI</th>
                    <th className="px-4 py-3">Add Date</th>
                    <th className="px-4 py-3">Update Date</th>
                  </tr>
                </thead>

                <tbody className="bg-white">
                  {tableData?.data?.map((item, index) => (
                    <tr key={item.id} className="border-b  hover:bg-gray-50">
                      <td className="pl-4 py-1">{index + 1}</td>

                      <td className=" py-1">
                        <button
                          onClick={() => handleEdit(item)}
                          className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
                        >
                          Edit
                        </button>
                      </td>
                      <td className=" py-1">
                        <button
                          onClick={() => {
                            setShowConfirmModal(true);
                            setIdToDelete(item.id);
                          }}
                          className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                        >
                          Delete
                        </button>
                      </td>

                      <td className="px-4 py-1">{item.ifsc}</td>
                      <td className="px-4 py-1">{item.code || "-"}</td>
                      <td className="px-4 py-1">{item.bank}</td>
                      <td className="px-4 py-1">{item.branch}</td>
                      <td className="px-4 py-1">{item.centre}</td>
                      <td className="px-4 py-1">{item.district}</td>
                      <td className="px-4 py-1">{item.state}</td>
                      <td className="px-4 py-1">{item.city}</td>
                      <td className="px-4 py-1">{item.micr}</td>
                      <td className="px-4 py-1">{item.swift || "-"}</td>
                      <td className="px-4 py-1">{item.iso3166}</td>
                      <td className="px-4 py-1">{item.contact}</td>
                      <td className="px-4 py-1 align-middle">
                        <div className="flex items-center justify-center gap-2">
                          {item?.address
                            ? item?.address?.slice(0, 5) + "..."
                            : "-"}
                          {item?.address && (
                            <button
                              onClick={() =>
                                openViewer("address", item.address)
                              }
                            >
                              <Eye className="text-blue-600" />
                            </button>
                          )}
                        </div>
                      </td>

                      <td className="px-4 py-1">{item.imps ? "Yes" : "No"}</td>
                      <td className="px-4 py-1">{item.neft ? "Yes" : "No"}</td>
                      <td className="px-4 py-1">{item.rtgs ? "Yes" : "No"}</td>
                      <td className="px-4 py-1">{item.upi ? "Yes" : "No"}</td>

                      <td className="px-4 py-1">
                        {new Date(item.created_at)?.toLocaleString() || "-"}
                      </td>
                      <td className="px-4 py-1">
                        {new Date(item.updated_at).toLocaleString() || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {loading && (
                <div className="absolute inset-0 bg-black/50 text-blue-600 flex justify-center items-center">
                  <Loader2 size={75} className="animate-spin" />
                </div>
              )}
            </div>

            <div className="flex gap-4 mt-4 items-center w-full justify-center">
              <button
                onClick={() => setCurrentPage((prev) => prev - 1)}
                disabled={currentPage === 1}
                className="text-white bg-blue-600 px-3 py-2 rounded-md disabled:opacity-50"
              >
                prev
              </button>
              Page {tableData?.pagination?.current_page} of{" "}
              {tableData?.pagination?.last_page}
              <button
                onClick={() => setCurrentPage((prev) => prev + 1)}
                disabled={currentPage === tableData?.pagination?.last_page}
                className="text-white bg-blue-600 px-3 py-2 rounded-md  disabled:opacity-50"
              >
                next
              </button>
            </div>
          </div>
        </div>
      </div>

      <InfoViewerModal
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        label={viewerData.label}
        value={viewerData.value}
      />

      <ConfirmModal
        onConfirm={handleDelete}
        isOpen={showConfirmModal}
        loading={isDeleting}
        onCancel={() => setShowConfirmModal(false)}
      />
    </div>
  );
}
